import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import QuoteForm from '../../components/feature/QuoteForm';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About NWA Roofing | Connecting Homeowners with Licensed Contractors';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'NWA Roofing connects Northwest Arkansas homeowners with trusted, licensed roofing contractors. Get free quotes from vetted professionals in Fayetteville, Springdale, Rogers, Bentonville for storm damage repair, roof replacement, and insurance claims.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'NWA roofing referral, Fayetteville roofer quotes, licensed roofing contractor, Northwest Arkansas roofing');
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />
      
      {/* Hero Section - Mobile Optimized */}
      <header className="relative min-h-[50vh] sm:min-h-[60vh] lg:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 sm:pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a96b5bf71ad6b583500f63cbda20bb7f.jpg"
            alt="Professional roofing team in Northwest Arkansas"
            title="Trusted roofing contractors NWA"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center py-12 sm:py-0">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight px-4">
            Connecting NWA Homeowners with Trusted Roofing Experts
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white max-w-4xl mx-auto leading-relaxed px-4">
            25+ years of excellence in <strong>roofing services</strong> throughout Northwest Arkansas
          </p>
        </div>
      </header>

      {/* Our Story Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <img
                src="https://readdy.ai/api/search-image?query=beautiful%20residential%20neighborhood%20in%20Northwest%20Arkansas%20with%20well-maintained%20homes%20featuring%20quality%20roofing%20installations%2C%20aerial%20view%20showing%20community%20and%20craftsmanship%2C%20bright%20daylight%20with%20clear%20sky%2C%20no%20vehicles%20or%20branding%20visible%20in%20frame&width=800&height=600&seq=nwa-about-community&orientation=landscape"
                alt="Northwest Arkansas residential roofing community"
                title="Quality roofing installations NWA"
                className="rounded-2xl sm:rounded-3xl shadow-2xl w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-orange-600 font-semibold uppercase tracking-wider text-xs sm:text-sm mb-2 sm:mb-3">OUR STORY</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
                Your Trusted Connection to Quality Roofing
              </h2>
              <div className="space-y-4 sm:space-y-6 text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed">
                <p>
                  For over 25 years, we've been connecting Northwest Arkansas homeowners with the region's most trusted and skilled <strong>roofing contractors</strong>. Our mission is simple: ensure every homeowner has access to quality <strong>roofing services</strong>, transparent pricing, and exceptional craftsmanship.
                </p>
                <p>
                  We understand that your roof is one of your home's most critical investments. That's why we've built a network of certified, licensed, and insured contractors who specialize in everything from <strong>storm damage repair</strong> and <strong>hail damage restoration</strong> to complete <strong>roof replacement</strong> and <strong>emergency repairs</strong>.
                </p>
                <p>
                  Every contractor in our network undergoes rigorous vetting to ensure they meet our high standards for quality, reliability, and customer service. When you work with us, you're not just getting a roof repair or replacement—you're getting peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
              Why Homeowners Trust Our Network
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-shield-check-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Licensed & Insured</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Every contractor in our network is fully licensed, insured, and bonded for your protection and peace of mind.
              </p>
            </article>

            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-star-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Quality Guaranteed</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                We only work with contractors who maintain the highest standards of workmanship and customer satisfaction.
              </p>
            </article>

            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-time-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Fast Response</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                24/7 emergency services available. Most contractors respond within 2-4 hours for urgent situations.
              </p>
            </article>

            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-hand-coin-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Transparent Pricing</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                No hidden fees or surprise charges. Get detailed, upfront estimates before any work begins.
              </p>
            </article>

            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-map-pin-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Local Expertise</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                Our contractors know NWA weather patterns and building codes inside and out.
              </p>
            </article>

            <article className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow touch-manipulation">
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4 sm:mb-6">
                <i className="ri-customer-service-2-line text-2xl sm:text-3xl text-blue-600" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Exceptional Service</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                From initial inspection to final cleanup, expect professional service at every step.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            Ready to Connect with a Trusted Contractor?
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white mb-6 sm:mb-8 leading-relaxed px-4">
            Get your <strong>free inspection</strong> today and experience the difference of working with Northwest Arkansas's best roofing professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-lg mx-auto">
            <a
              href="tel:4793455312"
              className="bg-white text-blue-900 px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-blue-50 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
              aria-label="Call for free inspection"
            >
              <i className="ri-phone-fill mr-2"></i>
              Call (479) 345-5312
            </a>
            <Link
              to="/contact"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-xl flex items-center justify-center whitespace-nowrap touch-manipulation active:scale-95"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;